// @ts-nocheck

import { mergeWith, isArray } from 'lodash';

import { CommonActionTypes } from '../../../types/actions/common';
import { type BetslipStatus } from '../../../types/betslip';
import { BoomStateActionTypes } from '../types/actions/boom';
import { type BoomGlobalStatus } from '../types/global';
import { type BoomSkinId } from '../types/global';
import { type BoomNotificationState } from '../types/notification';
import { type BoomStepId } from '../types/steps';
import { type BoomStepStatus } from '../types/steps';
import { type BoomUserStatus } from '../types/user';

export type BoomGlobalState = {
  boomId: string; // Id d'opé
  skinId: BoomSkinId; // Halloween
  status?: BoomGlobalStatus;
  version?: number | null;
  maxWinningAmount?: number | null;
  stakeMaxRefAmount?: number | null;
  operationStartDate: number;
  operationEndDate: number;
};

export type BoomUserStepProgressState = {
  status: BoomStepStatus;
  wonAmount?: number | null;
  betslipReference: string | null;
};

export type BoomUserProgress = {
  [stepId: BoomStepId]: BoomUserStepProgressState;
};

export type BoomUserBetslips = {
  [betslipReference: string]: {
    betslipStatus: BetslipStatus;
    validationDate: number;
    betslipId: number;
    betAmount: number;
    matchIds: Array<number>;
    odds: number;
  };
};

export type BoomUserState = {
  status: BoomUserStatus;
  progress: BoomUserProgress;
  notifications: Array<BoomNotificationState>;
  betslips: BoomUserBetslips;
};

export type BoomStepState = {
  stepId: BoomStepId;
  stakeMultiplier: null | number;
  eligibility: unknown;
};

export type BoomSteps = {
  [stepId: BoomStepId]: BoomStepState;
};

export type BoomMiscState = {
  tmpNotifications?: Array<BoomNotificationState>;
};

export type BoomState = {
  global: BoomGlobalState;
  stepsOrder: Array<BoomStepId>;
  steps: BoomSteps;
  user: BoomUserState;
  misc: BoomMiscState;
};

export const boomInitialState: BoomState = {
  global: {},
  steps: {},
  stepsOrder: [],
  user: {},
  misc: {},
};

const actionTypesToReactOn = [
  CommonActionTypes.RECEIVE_ENTITIES,
  BoomStateActionTypes.UPDATE_STATE,
  BoomStateActionTypes.FILL_TEMPORARY_NOTIFICATIONS,
  BoomStateActionTypes.SKIP_CURRENT_TMP_NOTIFICATION,
];

export default (state: BoomState = boomInitialState, action: unknown) => {
  const { type, payload } = action;
  const { global, user, misc, steps, stepsOrder } = state;

  if (actionTypesToReactOn.indexOf(type) !== -1) {
    if (type === BoomStateActionTypes.SKIP_CURRENT_TMP_NOTIFICATION) {
      const { tmpNotifications } = misc;
      if (tmpNotifications) {
        const [, ...next]: Array<unknown> = tmpNotifications;
        const copy = JSON.parse(JSON.stringify(next));

        return {
          ...state,
          misc: {
            ...misc,
            tmpNotifications: copy,
          },
        };
      }
    }

    if (type === BoomStateActionTypes.FILL_TEMPORARY_NOTIFICATIONS) {
      const { notifications = [] } = user ?? {};
      const copy = JSON.parse(JSON.stringify(notifications));

      return {
        ...state,
        misc: {
          ...misc,
          tmpNotifications: copy,
        },
      };
    }

    if (type === BoomStateActionTypes.UPDATE_STATE) {
      const { userStateMutation } = payload;

      const nextUserState = {};
      mergeWith(nextUserState, user, userStateMutation, (objValue, srcValue) => {
        if (isArray(srcValue)) {
          return srcValue;
        }
      });

      return {
        ...state,
        user: nextUserState,
      };
    }

    if (type === CommonActionTypes.RECEIVE_ENTITIES) {
      const { settings } = payload;
      const { boom } = settings ?? {};

      const {
        boomId,
        skinId,
        version,
        maxWinningAmount,
        stakeMaxRefAmount,
        status,
        operationStartDate,
        operationEndDate,
      } = boom ?? {};

      return {
        ...state,
        steps: boom === null ? boomInitialState.steps : boom?.steps ?? steps,
        stepsOrder: boom === null ? boomInitialState.stepsOrder : boom?.stepsOrder ?? stepsOrder,
        global: {
          ...global,
          boomId: boom === null ? undefined : boomId ?? global.boomId,
          skinId: boom === null ? undefined : skinId ?? global.skinId,
          status: boom === null ? undefined : status ?? global.status,
          version: boom === null ? undefined : version ?? global.version,
          maxWinningAmount: boom === null ? undefined : maxWinningAmount ?? global.maxWinningAmount,
          stakeMaxRefAmount: boom === null ? undefined : stakeMaxRefAmount ?? global.stakeMaxRefAmount,
          operationStartDate: boom === null ? undefined : operationStartDate ?? global.operationStartDate,
          operationEndDate: boom === null ? undefined : operationEndDate ?? global.operationEndDate,
        },
      };
    }
  }

  return state;
};
